import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react'

import type { BusinessCreate } from 'src/features/business/shared/schemas/business'
import AnimatedLynkIcon from 'src/features/lynk/components/animated-lynk-icon'
import { LYNK_WORKFLOW_DATA } from 'src/features/lynk/schemas/workflows'
import type { LynkWorkflow } from 'src/features/lynk/schemas/workflows'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { ProfessionEnum } from 'src/shared/features/users/schemas/profession'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

import { useFirstWorkflowPreview, WorkflowPreviewStatus } from '../../api/use-first-workflow-preview'
import BCWorkflowCard from '../../components/bc-workflow-card'
import BusinessFormTitle from '../../components/business-form-title'
import { useBusinessCreateContext } from '../../context/business-create-context'
import AddMissingInfoModal from '../../modals/add-missing-info-modal'
import MissingDataModal from '../../modals/missing-data-modal'
import { useBusinessCreateStore } from '../../store/business-create-store'

const WORKFLOW_CARD_WIDTH = 210
const WORKFLOW_CARD_GAP = 60

const BusinessCreateWorkflowsStep = () => {
  const { formData, setFormData } = useBusinessCreateStore()
  const { handleNext, setFooterConfig } = useBusinessCreateContext()
  const { toast } = useToast()
  const { data: user } = useMyUserData()
  const [isMissingDataModalVisible, setIsMissingDataModalVisible] = useState(false)
  const [isAddInfoModalVisible, setIsAddInfoModalVisible] = useState(false)
  const [currentWorkflowIndex, setCurrentWorkflowIndex] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [displayedWorkflowDescription, setDisplayedWorkflowDescription] = useState('')
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true)
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const hasSetInitialIndexRef = useRef(false)
  const descriptionFadeTimeoutRef = useRef<number | null>(null)
  const workflowPreviewPayload = useMemo<BusinessCreate>(() => formData, [formData])

  const { data: firstWorkflowPreview, refetch: refetchFirstWorkflowPreview } =
    useFirstWorkflowPreview(workflowPreviewPayload)

  const workflowPaths = useMemo(() => firstWorkflowPreview?.workflows || [], [firstWorkflowPreview])
  const currentWorkflow = workflowPaths[currentWorkflowIndex] ?? null
  const connectorLength = WORKFLOW_CARD_GAP / 2 + 1
  const horizontalInset = Math.max(0, (viewportWidth - WORKFLOW_CARD_WIDTH) / 2)
  const workflowScrollPitch = WORKFLOW_CARD_WIDTH + WORKFLOW_CARD_GAP
  const activeWorkflowDescription = useMemo(() => {
    if (!currentWorkflow) return ''
    const workflowData = LYNK_WORKFLOW_DATA[currentWorkflow.name]
    if (!workflowData) return ''

    if (user?.profession === ProfessionEnum.MORTGAGE_LOAN_OFFICER) {
      return workflowData.descriptionLO || workflowData.description
    }

    return workflowData.description
  }, [currentWorkflow, user?.profession])

  const continueWithWorkflow = useCallback(
    (startingWorkflow: LynkWorkflow | null) => {
      setFormData({
        ...formData,
        startingWorkflow,
      })
      handleNext()
    },
    [formData, handleNext, setFormData],
  )

  const scrollToWorkflowIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const viewport = scrollViewportRef.current
      if (!viewport) return

      viewport.scrollTo({
        left: index * workflowScrollPitch,
        behavior,
      })
    },
    [workflowScrollPitch],
  )

  const onPressTurnOffAndContinue = useCallback(() => {
    continueWithWorkflow(null)
  }, [continueWithWorkflow])

  const onPressContinue = useCallback(() => {
    if (!currentWorkflow) {
      handleNext()
      return
    }

    if (currentWorkflow.status === WorkflowPreviewStatus.COMPLETE) {
      const nextNonCompleteIndex = workflowPaths.findIndex(
        (workflow, index) => index > currentWorkflowIndex && workflow.status !== WorkflowPreviewStatus.COMPLETE,
      )

      if (nextNonCompleteIndex >= 0) {
        toast({
          title: 'Workflow complete',
          description: 'Moving to the next workflow that needs attention.',
        })
        setCurrentWorkflowIndex(nextNonCompleteIndex)
        scrollToWorkflowIndex(nextNonCompleteIndex)
        return
      }

      toast({
        title: 'Workflow complete',
        description: 'No additional workflows need action.',
      })
      return
    }

    if (currentWorkflow.status === WorkflowPreviewStatus.MISSING_DATA) {
      setIsMissingDataModalVisible(true)
      return
    }

    continueWithWorkflow(currentWorkflow.name)
  }, [
    continueWithWorkflow,
    currentWorkflow,
    currentWorkflowIndex,
    handleNext,
    scrollToWorkflowIndex,
    toast,
    workflowPaths,
  ])

  const onAddManually = useCallback(() => {
    setIsMissingDataModalVisible(false)
    setIsAddInfoModalVisible(true)
  }, [])

  const onLetLynkHandle = useCallback(() => {
    setIsMissingDataModalVisible(false)
    continueWithWorkflow(currentWorkflow?.name ?? null)
  }, [continueWithWorkflow, currentWorkflow?.name])

  const onFilledAllMissingFields = useCallback(() => {
    setIsAddInfoModalVisible(false)
    refetchFirstWorkflowPreview()
  }, [refetchFirstWorkflowPreview])

  useEffect(() => {
    setFooterConfig({
      buttonText: 'Continue',
      primaryAction: onPressContinue,
      secondaryButton: {
        text: 'Turn off & continue',
        onClick: onPressTurnOffAndContinue,
      },
    })

    return () => {
      setFooterConfig(null)
    }
  }, [onPressContinue, onPressTurnOffAndContinue, setFooterConfig])

  useEffect(() => {
    const viewport = scrollViewportRef.current
    if (!viewport) return

    const updateViewportWidth = () => {
      setViewportWidth(viewport.clientWidth)
    }

    updateViewportWidth()
    window.addEventListener('resize', updateViewportWidth)
    return () => {
      window.removeEventListener('resize', updateViewportWidth)
    }
  }, [workflowPaths.length])

  useEffect(() => {
    if (!workflowPaths.length || hasSetInitialIndexRef.current) return

    const firstNonCompleteIndex = workflowPaths.findIndex(
      (workflow) => workflow.status !== WorkflowPreviewStatus.COMPLETE,
    )
    const targetIndex = firstNonCompleteIndex >= 0 ? firstNonCompleteIndex : 0

    setCurrentWorkflowIndex(targetIndex)
    requestAnimationFrame(() => {
      scrollToWorkflowIndex(targetIndex, 'auto')
    })

    hasSetInitialIndexRef.current = true
  }, [scrollToWorkflowIndex, workflowPaths])

  useEffect(() => {
    if (!activeWorkflowDescription) {
      setDisplayedWorkflowDescription('')
      setIsDescriptionVisible(true)
      return
    }

    if (!displayedWorkflowDescription) {
      setDisplayedWorkflowDescription(activeWorkflowDescription)
      setIsDescriptionVisible(true)
      return
    }

    if (activeWorkflowDescription === displayedWorkflowDescription) return

    setIsDescriptionVisible(false)

    if (descriptionFadeTimeoutRef.current) {
      window.clearTimeout(descriptionFadeTimeoutRef.current)
    }

    descriptionFadeTimeoutRef.current = window.setTimeout(() => {
      setDisplayedWorkflowDescription(activeWorkflowDescription)
      setIsDescriptionVisible(true)
      descriptionFadeTimeoutRef.current = null
    }, 180)

    return () => {
      if (descriptionFadeTimeoutRef.current) {
        window.clearTimeout(descriptionFadeTimeoutRef.current)
        descriptionFadeTimeoutRef.current = null
      }
    }
  }, [activeWorkflowDescription, displayedWorkflowDescription])

  const onWorkflowViewportScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!workflowPaths.length) return
    const index = Math.round(event.currentTarget.scrollLeft / workflowScrollPitch)
    const nextIndex = Math.max(0, Math.min(index, workflowPaths.length - 1))
    if (nextIndex !== currentWorkflowIndex) {
      setCurrentWorkflowIndex(nextIndex)
    }
  }

  return (
    <>
      <div className="flex size-full flex-col items-center justify-start gap-5 pt-[90px]">
        <AnimatedLynkIcon variant="gradient" width={100} height={100} />
        <BusinessFormTitle>Get started with</BusinessFormTitle>
        <p
          className={cn(
            'h-[90px] max-w-[600px] px-4 text-center text-sm leading-[1.6] text-muted transition-opacity duration-200',
            isDescriptionVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          {displayedWorkflowDescription}
        </p>

        <div className="w-full max-w-[760px]">
          {workflowPaths.length === 0 ? (
            <div className="mt-4 text-center text-sm text-muted">No workflows available yet.</div>
          ) : (
            <>
              <div
                ref={scrollViewportRef}
                onScroll={onWorkflowViewportScroll}
                className="w-full snap-x snap-mandatory overflow-x-auto scroll-smooth scrollbar-none"
              >
                <div
                  className="flex items-start gap-[60px] pb-1"
                  style={{
                    width: 'max-content',
                    paddingLeft: horizontalInset,
                    paddingRight: horizontalInset,
                  }}
                >
                  {workflowPaths.map((workflow, index) => (
                    <button
                      key={`${workflow.name}-${index}`}
                      className="shrink-0 snap-center pt-5"
                      style={{ width: WORKFLOW_CARD_WIDTH }}
                      onClick={() => scrollToWorkflowIndex(index)}
                    >
                      <BCWorkflowCard
                        workflow={workflow}
                        isFirst={index === 0}
                        isLast={index === workflowPaths.length - 1}
                        connectorLength={connectorLength}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <MissingDataModal
        open={isMissingDataModalVisible}
        onClose={() => setIsMissingDataModalVisible(false)}
        missingFields={currentWorkflow?.missingFields || []}
        onAddManually={onAddManually}
        onLetLynkHandle={onLetLynkHandle}
      />

      <AddMissingInfoModal
        open={isAddInfoModalVisible}
        onClose={() => setIsAddInfoModalVisible(false)}
        fields={currentWorkflow?.missingFields || []}
        onAllCompleted={onFilledAllMissingFields}
      />

      <form id="business-create-form" onSubmit={(event) => event.preventDefault()} />
    </>
  )
}

export default BusinessCreateWorkflowsStep
